import MainContentHeader from './layout/MainContentHeader.js';
import OrderSummaryComponentList from './OrderSummaryComponentList.jsx';
import CategoryPanel from './CategoryPanel.jsx';
import * as API from '../api/Service.js'
import { useQuery } from "@tanstack/react-query"
import { useState, useRef, useContext, useEffect, useMemo } from 'react';
import '../assets/scss/components/maincontent.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import parse from 'html-react-parser';
import Carousel from 'react-bootstrap/Carousel';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import lodash from 'lodash';
import { EmbedSiteShortContext } from '../App.js';
import PaymentInfoModals from './common/PaymentInfoModals.jsx';
import { useErrorBoundary } from "react-error-boundary";
import * as Utils from '../common/Utils.js';
import ViewingGallery from './common/ViewingGallery.jsx';

// Flow:
// Component code fully runs - not much goes on - viewmodel is just emptyViewModel (stops errors) - no other child components are rendered
// The ConfigData API call then takes place - gets a build - response sets the view model and kicks of a re-render
// Viewmodel now has categories and renders the categories in the same way as above - blank intially but make calls to get their options - the re-renders with options
// When an option is clicked, the chosen option is fed back up the chain to the main viewModel which changes itself accordingly and forces a re-render to update the build via API call
// The re-render then shows spinners on each catgeory because of flags set on the viewModel when it was changed in response to an option being selected, meanwhile waiting for build/update call
// When build/update call finished the viewModel is then re-set and so re-render happens again in the same way as on line 3
// Categories must always re-render / re-pull its options in response to an option/component change to enable the compatiblity checking take place

export default function MainContent(props)
{

    //console.log("Main Content");

    const site = useContext(EmbedSiteShortContext);

    // set up state variable to manually trigger render when needed
    const [reRenderBodge, setReRenderBodge] = useState(false);
    const [displayExpertModeModal, setDisplayExpertModeModal] = useState(false);

    //#region Setup viewModel

    // need this to allow pre-render of page before data has been async'd in
    // as the markup logic requires a populated object to work with
    const emptyViewModel = {
        "prebuilt": {
            "productID": 0,
        },
        "build": {
            "id": 0
        },
        "buildID": 0,
        "totalCostText": "",
        "buildItemSummary": null,
        "options": {
            "isEditing": false,
            "isExpertMode": false,
            "forceSelection": false,
            "removeFromBasket": null,
            "removeFromBasketBuildID": null,
            "isTest": false
        },
        "settings": {

        },
        "images": [],
        "incompatibility": null,
        "isExpress": false
    };

    const emptySessionModel = {
        "showIncompatibleOnCategories": {

        },
        "selectedOptionsBeforeCatRefetching": []
    };

    const emptyCategoryModel = {
        "id": 0,
        "options": []
    }

    const sessionModel = useRef(emptySessionModel);

    // use useRef instead of useState to prevent re-render being triggered
    // use setReRenderBodge(true) if a viewModel re-render is required
    // previousViewModel refers to the state of the viewModel before a different option has been clicked ...
    // ...(useful when handling the cancel of a selected incompatible option)
    const previousViewModelRef = useRef(emptyViewModel); // use previousViewModelRef.current to get and set values

    const viewModelRef = useRef(emptyViewModel); // use viewModelRef.current to get and set values
    let viewModel = viewModelRef.current; // nicer name variable - get values only

    let isTest = false; // configurator rules - test mode
    let expertMode = false;
    for (const qKey of new URLSearchParams(window.location.search).keys())
    {
        if (qKey === "expert")
        {
            expertMode = true;
        }
        else if (qKey === "istest")
        {
            isTest = true;
        }
    }

    const { showBoundary } = useErrorBoundary();

    // --- Dopple pricing setup ---
    const [doppleText, setDoppleText] = useState("Spread the cost on orders over £1000");
    const [doppleWebApiSiteIDParam, setDoppleWebApiSiteIDParam] = useState(null);

    // need to flip siteid when talking to website
    useEffect(() => {
        if (site === "ccl")
        {
            setDoppleWebApiSiteIDParam(1);
        }
        else
        {
            setDoppleWebApiSiteIDParam(2);
        }
    }, [site]);

    // locally cache url until viewModel.totalCostText changes
    const url = useMemo(() => {
        return viewModel.totalCostText
            ? `${process.env.REACT_APP_WEB_APP_API_URL}/Dopple/GetLowestFinancePlan/${doppleWebApiSiteIDParam}?grossPrice=${viewModel.totalCostText}`
            : "";
    }, [viewModel.totalCostText, doppleWebApiSiteIDParam]);

    const FetchDopplePricingOptions = {
        method: 'GET',
        mode: 'cors', // no-cors, *cors, same-origin
        credentials: 'include', // include, *same-origin, omit
        headers: {
            'embed-site': process.env.REACT_APP_EMBED_SITE
        },
    }

    const fetchDopplePricingRefetch = API.useRefetchQuery("FetchDopplePricing", url, 3, FetchDopplePricingOptions);

    useEffect(() =>
    {
        if (!viewModel.dopplePay) return;
        if (!viewModel.totalCostText || !fetchDopplePricingRefetch) return;

        if (viewModel.totalCostText < 1000)
        {
            setDoppleText(`Spread the cost on orders over £1000`);
            return;
        }

        fetchDopplePricingRefetch()
            .then((response) =>
            {
                if (response?.data?.errors && Object.keys(response.data.errors).length > 0)
                {
                    throw new Error(JSON.stringify(response));
                }

                const data = response.data;

                if (data.message === "Failed to fetch" || data.monthlyPayment === 0)
                {
                    setDoppleText(`Spread the cost on orders over £1000`);
                }
                else
                {
                    let monthlyPaymentText = `From £${data.monthlyPayment.toFixed(2)} per month`;

                    if (data.minimumZeroInterestValue > 0) // 0 assumes minimumZeroInterestValue not set
                    {
                        let trailingText = `from £${data.minimumZeroInterestValue}`;

                        if (viewModel.totalCost >= data.minimumZeroInterestValue)
                        {
                            trailingText = 'for this build';
                        }

                        setDoppleText(`${monthlyPaymentText} | 0% APR available ${trailingText}`);
                    }
                    else
                    {
                        setDoppleText(monthlyPaymentText);
                    }
                }
            })
            .catch((error) =>
            {
                showBoundary(error);
            });
    }, [viewModel.dopplePay, viewModel.doppleZeroInterest, viewModel.totalCostText, fetchDopplePricingRefetch, showBoundary]); // re-run when price changes

    // --- Dopple pricing setup end ---

    // set up query to get initial viewModel i.e. the Build
    // this auto runs after component load has finished - but doesn't run on re-render
    useQuery({
        queryKey: ["ConfigData"],
        queryFn: async () =>
        {

            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            let loadFromUrl = false;
            let loadFromPage = false;
            let responseData = "";

            let buildID = 0;
            let prebuiltSku = "";
            let editMode = false;
            let viewMode = false;

            let apiUrl = "";

            //console.log("initial api call started");

            for (const key of urlParams.keys())
            {
                switch (key)
                {

                    // -----------

                    // loading a build - could be locked (in basket/ordered) or unlocked (shared/bookmarked)
                    case "buildid":
                        loadFromUrl = true;
                        buildID = urlParams.get('buildid');
                        break;

                    // starting with a prebuild
                    case "prebuilt":
                        loadFromUrl = true;
                        prebuiltSku = urlParams.get('prebuilt');
                        break;

                    // ----------- 

                    // edit flag to indicate whether we're editing a product from the basket - pass to server with api call
                    case "edit":
                        editMode = true;
                        break;

                    // view flag to indicate whether we're viewing a product from the basket
                    case "view":
                        viewMode = true;
                        break;

                    default:
                        break;

                }
            }

            let querystringFragments = [];

            if (editMode || viewMode) // if in view mode, we clone config anyway, so need to capture old build ID in case the customer clicks edit
            {
                querystringFragments.push('edit=true');
            }

            if (expertMode)
            {
                querystringFragments.push('expert=true');
            }

            if (isTest)
            {
                querystringFragments.push('istest=true');
            }


            if (loadFromUrl)
            {

                if (buildID > 0)
                {

                    if (querystringFragments.length > 0)
                    {
                        apiUrl = "/build/" + buildID + "?" + querystringFragments.join('&');
                    }
                    else
                    {
                        apiUrl = "/build/" + buildID;
                    }

                }
                else if (prebuiltSku !== "")
                {
                    if (querystringFragments.length > 0)
                    {
                        apiUrl = "/prebuilt/" + prebuiltSku + "?" + querystringFragments.join('&');
                    }
                    else
                    {
                        apiUrl = "/prebuilt/" + prebuiltSku;
                    }
                }

            }
            else
            {

                // load prebuilt from hidden tag on page
                let itemSku = window.document.getElementById("itemSku");

                if (itemSku !== null)
                {

                    let sku = itemSku.value;

                    let skuList = window.document.getElementById("skuList");
                    let qtyList = window.document.getElementById("qtyList");

                    if (skuList !== null && qtyList !== null)
                    {
                        querystringFragments.push(`skuList=${skuList.value}`);
                        querystringFragments.push(`qtyList=${qtyList.value}`);
                    }

                    loadFromPage = true;

                    if (querystringFragments.length > 0)
                    {
                        apiUrl = "/prebuilt/" + sku + "?" + querystringFragments.join('&');
                    }
                    else
                    {
                        apiUrl = "/prebuilt/" + sku;
                    }

                }

            }

            // load a default system in dev environment
            if (!loadFromUrl && !loadFromPage && process.env.NODE_ENV === 'development')
            {
                //apiUrl = "/prebuilt/HZN-R5-4600G-GAME";
                //apiUrl = "/prebuilt/CBA-I9-4090-FN"; // pre-applied vinyl
                //apiUrl = "/prebuilt/CBI-R5-GM"; // has upsell items (upsell #2)
                //apiUrl = "/prebuilt/HZN-I5-4060-GAME";
                //apiUrl = "/prebuilt/CL-TDINTEL-WGPU"; // Cube PC - no selections
                //apiUrl = "/prebuilt/CL-TDINTEL-WGPU?skuList=CPU0882,RAM5484,VGA6628&qtyList=1,1,1"; // Cube PC - selections
                apiUrl = '/prebuilt/CBS-EDGE-Y60';
            }

            if (apiUrl !== "")
            {
                // call API for initial load
                responseData = await API.sendGetRequest(apiUrl);
            }

            if (!responseData.buildID)
            {
                alert("Could not start system customisation. Please try again later.");
                return emptyViewModel;
            }
            else
            {
                urlParams.set("buildid", responseData.buildID);
                urlParams.delete("edit");

                window.history.replaceState(null, null, window.location.pathname + "?" + urlParams.toString());

                responseData.options.isUpdated = true;

                try
                {
                    responseData.bannersJSON = JSON.parse(responseData.bannersJSON);
                }
                catch (error)
                {
                    console.error('Error parsing banners JSON:', error);
                    responseData.bannersJSON = null;
                }

                viewModelRef.current = responseData;

                // probably not the best place to put it...
                document.title = "Configure your own " + responseData.prebuilt.name;

                return responseData;
            }
        },
        refetchOnWindowFocus: false
    });

    //#endregion Setup viewModel

    //#region Ramblings
    //  The result object contains a few very important states you'll need to be aware of to be productive. A query can only be in one of the following states at any given moment:

    //isLoading or status === 'loading' - The query has no data yet
    //isError or status === 'error' - The query encountered an error
    //isSuccess or status === 'success' - The query was successful and data is available
    //Beyond those primary states, more information is available depending on the state of the query:

    //error - If the query is in an isError state, the error is available via the error property.
    //data - If the query is in a success state, the data is available via the data property.
    //For most queries, it's usually sufficient to check for the isLoading state, then the isError state, then finally, assume that the data is available and render the successful state:


    // Mutations
    //const mutation = useMutation({
    //  mutationFn: API.sendPostRequest("/configuration", { data: "james" }),
    //  onSuccess: () => {
    //    // Invalidate and refetch
    //    useQueryClient.invalidateQueries({ queryKey: ['todos'] })
    //  },
    //})


    // see about mutations and go to core docs for more help on interactivty

    //#endregion Ramblings

    //console.log("viewModel before UpdateBuild is: ", JSON.parse(JSON.stringify(viewModel)));

    //#region Update Build

    let updateBuildRefetch = API.useRefetchQuery("UpdateBuild_" + viewModel.buildID, "/build/update", 2, viewModel);

    // TODO: figure out why multiple renders are happening - using this as workaround for now? 
    // may need to pass array of elements to listen for changes on and re-render on changes to those only see docs on ReactQuery
    const [viewModelIsUpdating, setViewModelIsUpdating] = useState(false);

    if (!viewModelIsUpdating && !viewModel.options.isUpdated && viewModel.buildID !== 0)
    {
        //console.log("triggering build update, viewModel is: ", JSON.parse(JSON.stringify(viewModel)));
        setViewModelIsUpdating(true);

        updateBuildRefetch()
            .then((response) =>
            {
                //console.log("response.data ", JSON.parse(JSON.stringify(response.data)));

                if (response?.data?.errors && Object.keys(response.data.errors).length > 0)
                {
                    throw new Error(JSON.stringify(response));
                }

                return response.data;
            })
            .then((data) =>
            {
                setViewModelIsUpdating(false);

                // show incompatibility modal if needed
                if (data.incompatibility)
                {
                    viewModelRef.current.options.isUpdated = true;
                    viewModelRef.current.options.forceSelection = false;
                    setIncompatibilities(data.incompatibility);
                    setShowIncompatibilityModal(true);
                }
                else
                {
                    data.options.isUpdated = true;
                    data.options.forceSelection = false;

                    // stop a blank space showing in the touched category's body when a user selects an option.
                    // INFO: the options for the touched category won't change, but updating the build fetches fresh categories
                    // with no options as the categories re-fetch their re-calc'd options themselves.
                    // the touched category then shwos 0 options when it can just use the existing model it had so just 
                    // updating the viewmodel here to use the existing touched category model to prevent the blank space showing.
                    if (categoryTouched.id !== 0)
                    {
                        const catIndex = data.categories.findIndex(c => c.id === categoryTouched.id);
                        if (catIndex !== -1)
                        {
                            data.categories[catIndex] = categoryTouched;
                        }
                    }

                    viewModelRef.current = data;
                    setShowIncompatibilityModal(false);
                }

                return true; // call finished
            })
            .catch((error) =>
            {
                //console.log("Main Content updateBuildRefetch error: ", JSON.parse(JSON.stringify(error)));
                showBoundary(error);
            });
    }

    //#endregion Update Build

    //#region Event Handlers

    // used to stopspinner from showing on category that was just interacted with
    const [categoryTouched, setCategoryTouched] = useState(emptyCategoryModel);

    // handle option click
    const updateCategory = (optionID, categoryModel, quantity, isQuantityChange, isSelected, isIncompatible) =>
    {
        // keep snapshot before option selected in case user needs to cancel selection of incompatible item
        previousViewModelRef.current = lodash.cloneDeep(viewModel);

        const modifiedCategoryIndex = previousViewModelRef.current.categories.findIndex((category) => { return category.id === categoryModel.id; });

        if (modifiedCategoryIndex && modifiedCategoryIndex > -1)
        {
            previousViewModelRef.current.categories[modifiedCategoryIndex] = lodash.cloneDeep(categoryModel);
        }

        // change quantity here to cater for different scenarios
        if (isSelected && quantity === 0)
        {
            quantity = 1; // if an item has just been selected, force quantity to be 1
        }

        if (!isSelected)
        {
            quantity = 0; // if item has been de-selected, force 0
        }

        if (quantity === 0)
        {
            // if we have changed a quantity to 0, always force a deselect!
            isQuantityChange = false;
        }

        //console.log("calling MainContent updateCategory with opt " + optionID + " and quantity: " + quantity + " and cat: " + JSON.parse(JSON.stringify(categoryModel)));
        //console.log("calling MainContent updateCategory viewModel before: ", JSON.parse(JSON.stringify(viewModel)));

        let postData = {
            "selectedOptions": [],
            "affectedCategories": [],
            "clickedOption": {
                "productId": 0,
                "quantity": 0,
                "name": "",
                "isMultiSelect": false,
                "isCompatible": false
            }
        };

        let selectedOption = "";

        for (var i = 0; i < viewModel.categories.length; i++)
        {
            // as a minimum - dirty *all categories as something has changed in the build
            // this will cause each category panel to re-pull it's options
            // don't dirty if fetching info against clicked incompatible option
            if (!isIncompatible)
                viewModel.categories[i].isUpdated = false;

            // cater for the category that has no options so the existing selected option name is shown
            if (viewModel.categories[i].options)
            {
                sessionModel.current.selectedOptionsBeforeCatRefetching[viewModel.categories[i].id] = null;
            }

            if (viewModel.categories[i].id === categoryModel.id)
            {
                // as this cateogry isn't being sent to the server for an update, the pricing needs sorting here
                let selectedOptionNetPrice = 0;
                let selectedOptionGrossPrice = 0;

                // ensure all options are there as initial loaded viewmodel will have the one selected
                viewModel.categories[i] = categoryModel;

                // mark the clicked option as selected/de-selected - at this point no versions of any model are tracking this
                for (var j = 0; j < viewModel.categories[i].options.length; j++)
                {

                    // 2. check to see if this option is the one that has been selected by the user
                    if (viewModel.categories[i].options[j].optionProductID === optionID)
                    {

                        // 3. set option selected to opposite of what it was - caters for multi select and de-select this way
                        if (!isQuantityChange)
                        {
                            viewModel.categories[i].options[j].isSelected = !viewModel.categories[i].options[j].isSelected;
                        }

                        viewModel.categories[i].options[j].selectedQuantity = quantity;

                        // this is set to false by server when the build is updated etc.
                        postData.clickedOption.productId = viewModel.categories[i].options[j].optionProductID;
                        postData.clickedOption.quantity = viewModel.categories[i].options[j].selectedQuantity;
                        postData.clickedOption.name = viewModel.categories[i].options[j].productName;
                        postData.clickedOption.isMultiSelect = viewModel.categories[i].allowMultipleSelection;
                        postData.clickedOption.isCompatible = viewModel.categories[i].options[j].severity !== 2;

                        // *Keep category with selected option in clean
                        viewModel.categories[i].isUpdated = true;
                        setCategoryTouched(categoryModel);

                        // set pricing (only if option hasn't just been deselected!)
                        if (viewModel.categories[i].options[j].isSelected)
                        {
                            selectedOptionNetPrice = viewModel.categories[i].options[j].netPrice;
                            selectedOptionGrossPrice = viewModel.categories[i].options[j].grossPrice;
                        }
                    }
                    else if (!viewModel.categories[i].allowMultipleSelection)
                    {
                        // set all other products to deselected if catetegory doesn't allow multi-select
                        viewModel.categories[i].options[j].isSelected = false;
                    }

                    // add selected/clicked item to list
                    if (viewModel.categories[i].options[j].isSelected)
                    {
                        selectedOption = {
                          "productId": viewModel.categories[i].options[j].optionProductID,
                          "quantity": viewModel.categories[i].options[j].selectedQuantity,
                          "netPrice": viewModel.categories[i].options[j].netPrice,
                          "grossPrice": viewModel.categories[i].options[j].grossPrice
                        };
                        postData.selectedOptions.push(selectedOption);
                    }

                    if (postData.affectedCategories.indexOf(viewModel.categories[i].id) === -1)
                        postData.affectedCategories.push(viewModel.categories[i].id);

                }

                // now we have the selected option price, calculate the price differences across the options
                for (var k = 0; k < viewModel.categories[i].options.length; k++)
                {
                    viewModel.categories[i].options[k].netPriceDifference = viewModel.categories[i].options[k].netPrice - selectedOptionNetPrice;
                    viewModel.categories[i].options[k].grossPriceDifference = viewModel.categories[i].options[k].grossPrice - selectedOptionGrossPrice;
                }
            }
            else
            {
                if ((!viewModel.categories[i].showInEasyMode && !viewModel.options.isExpertMode) || !viewModel.categories[i].options)
                    continue;

                for (var l = 0; l < viewModel.categories[i].options.length; l++)
                {
                    if (viewModel.categories[i].options[l].isSelected)
                    {
                        selectedOption = {
                            "productId": viewModel.categories[i].options[l].optionProductID,
                            "quantity": viewModel.categories[i].options[l].selectedQuantity,
                            "netPrice": viewModel.categories[i].options[l].netPrice,
                            "grossPrice": viewModel.categories[i].options[l].grossPrice
                        };
                        postData.selectedOptions.push(selectedOption);
                    }

                    if (postData.affectedCategories.indexOf(viewModel.categories[i].id) === -1)
                        postData.affectedCategories.push(viewModel.categories[i].id);

                }
            }

        }

        // queue re-render
        viewModel.options.isUpdated = false;
        viewModel.affectedCategories = postData.affectedCategories;
        viewModel.selectedOptions = postData.selectedOptions;
        viewModel.clickedOption = postData.clickedOption;
        //console.log("setViewModel updateCategory after:", JSON.parse(JSON.stringify(viewModel)));
        viewModelRef.current = viewModel;
        setReRenderBodge(!reRenderBodge);
        return false;
    }

    // handle updating viewModel after categoryModel updated
    const handleCategoryFetched = (fetchedCategory) =>
    {
        for (let i = 0; i < viewModel.categories.length; i++)
        {
            if (viewModel.categories[i].id === fetchedCategory.id)
            {
                viewModel.categories[i] = fetchedCategory;
                //console.log("setViewModel handleCategoryFetched: ", JSON.parse(JSON.stringify(viewModel)));
                viewModelRef.current = viewModel;
                setReRenderBodge(!reRenderBodge);
                break;
            }
        }
    }

    const handleExpertToggleFromWelcome = () =>
    {
        handleWelcomeModalClose();
        handleExpertModeToggle();
    }

    const requiredLoadedCategories = useRef(null);

    if (props.categoryItem.current !== null && viewModel.categories !== undefined && requiredLoadedCategories.current === null)
    {
        var category = props.categoryItem.current.rightPossibleCategory;
        var categoryIndex = viewModel.categories.findIndex(x => x.id === category);
        requiredLoadedCategories.current = viewModel.categories.slice(0, categoryIndex);
    }

    // handle Expert Mode being toggled on
    const handleExpertModeToggle = () =>
    {
        viewModel.options.isExpertMode = true;
        viewModel.options.isUpdated = false;

        let selectedOptions = [];
        let selectedOption = "";

        if (viewModel.categories != null)
        {
            for (var i = 0; i < viewModel.categories.length; i++)
            {
                // dirty all categories as extra options in every category may be available
                viewModel.categories[i].isUpdated = false;
                viewModel.categories[i].initialOptionProductID = -1;

                if ((!viewModel.categories[i].showInEasyMode && !viewModel.options.isExpertMode) || !viewModel.categories[i].options)
                    continue;

                for (var l = 0; l < viewModel.categories[i].options.length; l++)
                {
                    if (viewModel.categories[i].options[l].isSelected)
                    {
                        selectedOption = {
                            "categoryId": viewModel.categories[i].id,
                            "productId": viewModel.categories[i].options[l].optionProductID,
                            "quantity": viewModel.categories[i].options[l].selectedQuantity,
                            "netPrice": viewModel.categories[i].options[l].netPrice,
                            "grossPrice": viewModel.categories[i].options[l].grossPrice
                        };
                        selectedOptions.push(selectedOption);
                    }
                }
            }
        }

        viewModel.selectedOptions = selectedOptions;

        //console.log("setViewModel handleExpertModeToggle: ", JSON.parse(JSON.stringify(viewModel)));
        viewModelRef.current = viewModel;
        setReRenderBodge(!reRenderBodge);
    }

    // handle request for incompatible options on a given category
    const handleFetchIncompatibleOptions = (categoryId) =>
    {
        // Need this for showIncompatible flag to actually be set to true when rendering category panel
        // not sure why seems a bit magical - as if build update needs to be run to 'fully' re-render it
        // TODO: figure out why this is - yes maincontent will need to be rendered twice - after this and after category updates viewModel in handleCategoryFetched - bad!
        viewModel.options.isUpdated = false;

        for (var i = 0; i < viewModel.categories.length; i++)
        {
            if (viewModel.categories[i].id === categoryId)
            {
                // set flag to also pull incompatible options
                // viewModel.categories[i].showIncompatible = true;
                sessionModel.current.showIncompatibleOnCategories[viewModel.categories[i].id] = true;

                // dirty category to trigger refetch
                viewModel.categories[i].isUpdated = false;
                setCategoryTouched(viewModel.categories[i]);
                break;
            }
        }

        //console.log("setViewModel handleFetchIncompatibleOptions: ", JSON.parse(JSON.stringify(viewModel)));
        viewModelRef.current = viewModel;
        setReRenderBodge(!reRenderBodge);
    }

    const handleFetchCompatibleOptions = (categoryId) =>
    {
        viewModel.options.isUpdated = false;

        for (var i = 0; i < viewModel.categories.length; i++)
        {
            if (viewModel.categories[i].id === categoryId)
            {
                sessionModel.current.showIncompatibleOnCategories[viewModel.categories[i].id] = false;
                viewModel.categories[i].isUpdated = false;
                break;
            }
        }

        //console.log("setViewModel handleFetchCompatibleOptions: ", JSON.parse(JSON.stringify(viewModel)));
        viewModelRef.current = viewModel;
        setReRenderBodge(!reRenderBodge);
    }

    const handleDisplayExpertModeModalChange = () =>
    {
        setDisplayExpertModeModal(!displayExpertModeModal);
    }

    const displayAddComponentBtn = () =>
    {

        if (viewModel.incompatibility != null)
        {
            for (let i = 0; i < viewModel.incompatibility.incompatibleItems.length; i++)
            {
                if (viewModel.incompatibility.incompatibleItems[i].productID === viewModel.incompatibility.selectedProductID)
                {
                    return false;
                }
            }
        }

        return true;
    }

    //#endregion Event Handlers

    //#region Misc UI stuff

    // handle summary/incompatibility details expand/hide
    const [summaryExpanded, setSummaryExpanded] = useState("");
    const [incompatibilityDetailsExpanded, setIncompatibilityDetailsExpanded] = useState("");

    // take off hieght of header / dev notices
    const panelHeightOffsetClass = process.env.NODE_ENV === 'development' ? " height-including-messaging " : " height-excluding-messaging ";

    // incompatibility warning modal functionality
    const [showIncompatibilityModal, setShowIncompatibilityModal] = useState(false);

    // used to know if it should display the welcome modal
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);

    const [incompatibilities, setIncompatibilities] = useState(false);

    const [categoryIdExpanded, setCategoryIdExpanded] = useState(null);


    const handleCloseIncompatibilityModal = (forceSelection) =>
    {
        if (forceSelection)
        {
            viewModelRef.current.options.forceSelection = true;
            viewModelRef.current.options.isUpdated = false;

            // dirty every category as everything will need changing
            for (var i = 0; i < viewModelRef.current.categories.length; i++)
            {
                viewModelRef.current.categories[i].isUpdated = false;
            }
        }
        else
        {
            previousViewModelRef.current.options.isUpdated = true;
            viewModelRef.current = previousViewModelRef.current;
        }

        setIncompatibilityDetailsExpanded("");
        setShowIncompatibilityModal(false);
        setCategoryTouched(emptyCategoryModel);
    }

    const handleWelcomeModalClose = () =>
    {
        setShowWelcomeModal(false);
    }

    const summaryExpandBtnRef = useRef(null);

    const goToCategory = (categoryName) =>
    {
        // TODO: Convert this to useRef equivalent IF strange rendering bugs start occurring (05/23)
        // Stashed some code which was very close to working using useRef
        var category = document.getElementById(categoryName);

        if (category != null)
        {
            category.scrollIntoView({ behavior: 'smooth' });
        }

        if (window.innerWidth < 1200)
        {
            summaryExpandBtnRef.current.click();
        }
    }

    // create list of categories that are required
    let categoryInfo = {};

    if (viewModel.buildItemSummary)
    {
        let category;

        for (let i = 0; i < viewModel.buildItemSummary.categories.length; i++)
        {
            category = viewModel.buildItemSummary.categories[i];

            let itemNames = [];
            let isIntegrated = false;
            let isRequired = category.isRequired;

            for (let j = 0; j < category.items.length; j++)
            {

                let item = category.items[j];

                if (item.id !== 0)
                {
                    // real product / selected item
                    if (item.quantity > 1)
                    {
                        itemNames.push(item.name + " x " + item.quantity);
                    }
                    else
                    {
                        itemNames.push(item.name);
                    }
                }
                else
                {
                    // integrated on another component / bundles (e.g. stock cooler or graphics on cpu)
                    itemNames.push(item.name);
                    isIntegrated = true;
                }

            }

            categoryInfo[category.id] = { key: category.name, isIntegrated: isIntegrated, isRequired: isRequired, itemCount: itemNames.length, items: itemNames };
        }
    }

    // get/set selected option name on render of category so when it re-renders it can be still be used during the disruptive multi-rendering phase
    const getSelectedOptionName = (categoryId) =>
    {
        let item = sessionModel.current.selectedOptionsBeforeCatRefetching[categoryId];

        if (item)
            return sessionModel.current.selectedOptionsBeforeCatRefetching[categoryId];
        else
            return null; // make sure this value matches CategoryPanel's noneSelectedText const
    }

    const setSelectedOptionName = (categoryId, productName) =>
    {
        let item = sessionModel.current.selectedOptionsBeforeCatRefetching[categoryId];

        // only set once, this is cleared out on option change
        if (!item)
            sessionModel.current.selectedOptionsBeforeCatRefetching[categoryId] = productName ? productName : "";
    }

    const categoryMatchedName = useRef(null);

    // force view per site
    let gridListToggle = '2'; // list view by default
    let livePricingText = "Prices on this page are updated in real-time to give you the best possible experience. The price here may differ from the prices you see for the same or similar product elsewhere at Chillblast. Rest assured that the price you pay at checkout is correct, without strings or caveats.";

    if (site === "chillblast")
    {
        gridListToggle = '1'; // set to grid view for Chillblast
    }

    let summaryPanelColClass = "col-xl-6";
    let optionsPanelColClass = "col-xl-6";

    if (site === "ccl")
    {
        summaryPanelColClass = "col-xl-5";
        optionsPanelColClass = "col-xl-7";

        livePricingText = livePricingText.replace("Chillblast", "CCL");
    }

    // override prev/next image icon position
    const imageIndicatorsRef = useRef(null);

    if (imageIndicatorsRef.current && imageIndicatorsRef.current.element.children[2] && imageIndicatorsRef.current.element.children[3])
    {
        imageIndicatorsRef.current.element.children[2].style.cssText = "left: -" + ((imageIndicatorsRef.current.element.children[0].offsetWidth / 2) - 10 - 15) + "px;"; // + 10 for arrow width + 15 for padding
        imageIndicatorsRef.current.element.children[3].style.cssText = "right: -" + ((imageIndicatorsRef.current.element.children[0].offsetWidth / 2) - 10 - 15) + "px;";
    }

    const [inCCLDesktopMode, setInCCLDesktopMode] = useState((site === "ccl") && window.innerWidth >= 1200 ? true : false);

    // register resize event to change elements for ccl on desktop
    useEffect(() =>
    {
        function handleResize()
        {
            if (site === "ccl")
            {
                if (window.innerWidth >= 1200)
                {
                    setInCCLDesktopMode(true);
                }
                else
                {
                    setInCCLDesktopMode(false);
                }

                // re-calc chevrons on carousel on mobile
                if (imageIndicatorsRef.current && imageIndicatorsRef.current.element.children[2] && imageIndicatorsRef.current.element.children[3])
                {
                    imageIndicatorsRef.current.element.children[2].style.cssText = "left: -" + ((imageIndicatorsRef.current.element.children[0].offsetWidth / 2) - 10 - 15) + "px;"; // + 10 for arrow width + 15 for padding
                    imageIndicatorsRef.current.element.children[3].style.cssText = "right: -" + ((imageIndicatorsRef.current.element.children[0].offsetWidth / 2) - 10 - 15) + "px;";
                }
            }
        }

        window.addEventListener("resize", handleResize);

        // cleanup function
        return () =>
        {
            window.removeEventListener("resize", handleResize);
        };

    });

    // sticky cta
    const mainCTABtnOneRef = useRef(null);
    const mainCTABtnTwoRef = useRef(null);
    const mainLeftPanelRef = useRef(null);

    //#endregion Misc UI stuff

    let catCount = 0;

    // handle pc image being clicked
    const [viewImageIsActive, setViewImageIsActive] = useState(false);

    const handleViewImage = () =>
    {
        setViewImageIsActive(true);
    }

    const handleViewingGalleryClose = () =>
    {
        setViewImageIsActive(false);
    }

    // custom accordion stuff
    const [openCatId, setOpenCatId] = useState(null);
    const isTransitioning = useRef(false);

    const isInViewport = (element) =>
    {
        const rect = element.getBoundingClientRect();
        const viewHeight = window.innerHeight || document.documentElement.clientHeight;

        return (
            rect.top >= 0 &&
            rect.bottom <= viewHeight
        );
    }

    const handleToggleCategory = (categoryId, currentExpandedCategoryName, newHeaderRef) =>
    {
        setCategoryIdExpanded(categoryId);

        if (isTransitioning.current) return;

        const accordionContainer = document.getElementById("category-accordion");
        if (!accordionContainer) return;

        const currentExpandedContainer = document.getElementById(currentExpandedCategoryName);

        const scrollToRef = (ref) =>
        {
            if (ref?.current)
            {
                ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        };

        // 3 actions - closing current cat, opening new cat and closing current, or opening new cat
        if (openCatId === categoryId)
        {
            // Closing currently open item only

            // Close category
            setOpenCatId(null);

            // Wait for it to close before scrolling into view if now not in view
            setTimeout(() => {
                if (currentExpandedContainer && !isInViewport(currentExpandedContainer))
                {
                    currentExpandedContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }

                // allow another category to be toggled
                isTransitioning.current = false;
            }, 400);
        }
        else if (openCatId !== null)
        {
            // New cat is being opened whilst another is currently opened

            // Close category
            setOpenCatId(null);


            // Wait for close animation to finish
            setTimeout(() => {
                // Now scroll to the category being opened if now not in view...
                if (newHeaderRef.current && !isInViewport(newHeaderRef.current))
                {
                    scrollToRef(newHeaderRef);
                }

                // ...and begin to open it at the same time
                setOpenCatId(categoryId);

                // Wait for it to open before allowing toggle of any other category
                setTimeout(() => {
                    isTransitioning.current = false;
                }, 400);

            }, 600);
        }
        else
        {
            // Opening a cat when no others are open

            // Open category
            setOpenCatId(categoryId);

            // Don't do anything else as the category is already in view

            // Wait for it to open before allowing toggle of any other category
            setTimeout(() => {
                isTransitioning.current = false;
            }, 400);
        }
    }

    //#endregion Misc UI stuff

    //console.log("MainContent end - viewmodel: ", JSON.parse(JSON.stringify(viewModel)));

    return (
        <>
            {
                viewModel.buildID !== 0 &&
                <>
                    { /* MainContentHeader */}
                    <MainContentHeader isExpress={viewModel.isExpress} isExpertMode={viewModel.options.isExpertMode} forceEasyMode={viewModel.options.forceEasyMode} handleExpertModeToggle={handleExpertModeToggle} showExpertModeModal={displayExpertModeModal}
                                       handleExpertModeModalToggle={handleDisplayExpertModeModalChange} settings={viewModel.settings} set={viewModel.set}
                    />

                    <main className="container custom-bg-dark mx-0 mw-100 px-0">
                        <div className="row mx-auto">

                            {/* main left panel */}
                            <div ref={mainLeftPanelRef} className={"main-left-panel  custom-bg-vdark " + summaryPanelColClass + " py-3 py-lg-4 px-xl-5 text-center overflow-auto" + panelHeightOffsetClass}>
                                <div className={"main-left-inner-panel mx-auto " + (gridListToggle === '1' ? "ms-xl-auto me-xl-0 " : "") + "d-flex flex-column position-relative"}>
                                    <div>
                                        {
                                            (site === "chillblast")
                                            &&
                                            <h3 className="main-byo-name d-none d-xl-inline text-casing text-center text-secondary mb-1">Build Your Own</h3>
                                        }
                                        <h1 className="main-prebuilt-name text-center m-0 custom-color-light">{viewModel.prebuilt.name}</h1>
                                        {
                                            viewModel.isExpress ? <span className="d-block pb-4 pt-4 text-center"><FontAwesomeIcon className="pe-2" icon={icon({ name: 'truck', style: 'solid' })} />Delivered next working day for free when ordered before 1pm</span> : <span className="d-block mb-2 mb-sm-4"></span>
                                        }
                                    </div>

                                    {/* image carousel */}
                                    <div className={"main-prebuilt-image-container d-flex justify-content-center pb-5 pb-xl-0 " + (inCCLDesktopMode ? "d-none" : "")}>

                                        <div className="position-relative">

                                            <Carousel ref={imageIndicatorsRef} interval={null} indicators={viewModel.images.length > 1 ? true : false} indicatorLabels={[""]} className="cursor-pointer"
                                                      prevIcon={<span aria-hidden="true"><FontAwesomeIcon icon={icon({ name: 'chevron-left', style: 'solid' })} className={"prev-image-icon " + (viewModel.images.length < 2 ? "d-none" : "")} /></span>}
                                                      nextIcon={<span aria-hidden="true"><FontAwesomeIcon icon={icon({ name: 'chevron-right', style: 'solid' })} className={"next-image-icon " + (viewModel.images.length < 2 ? "d-none" : "")} /></span>}
                                            >
                                                {
                                                    viewModel.images.map((imageUrl, index) =>
                                                    {
                                                        return (
                                                            <Carousel.Item key={"image_" + index}>
                                                                <img src={viewModel.settings.imageBaseUrl + imageUrl + "?width=1600&height=1600&scale=both"} className="pc-image d-block w-100 h-100" alt={viewModel.prebuilt.name + " image " + index} onClick={handleViewImage} />
                                                            </Carousel.Item>
                                                        )
                                                    })
                                                }
                                            </Carousel>

                                            {
                                                (viewModel.set.setID === 23) /* chillblast x v2 */
                                                &&
                                                <div className="position-absolute bottom-0 end-0">
                                                    <button className={"primary-button px-3 d-inline-block text-casing custom-color-dark "} type="button"
                                                            onClick={() =>
                                                            {
                                                                window.location.href = viewModel.clonedFrom ?
                                                                    `https://chillblast.modularcx.io/?casesku=${viewModel.caseSKU}&buildid=${viewModel.buildID}&clonedfrom=${viewModel.clonedFrom}` :
                                                                    `https://chillblast.modularcx.io/?casesku=${viewModel.caseSKU}&buildid=${viewModel.buildID}`;
                                                            }}>
                                                        Customise Design <FontAwesomeIcon icon={icon({ name: 'brush', style: 'solid' })} />
                                                    </button>
                                                </div>
                                            }

                                        </div>

                                    </div>

                                    {/* main order summary */}
                                    <div className={"main-order-summary-container d-flex flex-column custom-bg-dark p-3 px-sm-4 w-100" + (inCCLDesktopMode ? " h-auto mb-4 overflow-y-auto justify-content-start" : " justify-content-end summary-bar")}>
                                        <div className="main-order-summary-inner-container mx-auto w-100">

                                            <div className={"collapse collapse-order-summary mx-auto " + (inCCLDesktopMode ? "show" : "mb-3")} id="collapseOrderSummary">
                                                <div className="card border-0">
                                                    <p className="main-order-summary-build text-start text-casing text-silver-4 fw-normal mb-1">Build ID: {viewModel.buildID}</p>
                                                    <h3 className={"main-order-summary-name mb-1 custom-color-light " + (inCCLDesktopMode ? "d-none" : "")}>{viewModel.prebuilt.name}</h3>
                                                    {
                                                        (viewModel.buildID !== 0 && viewModel.buildItemSummary != null)
                                                        &&
                                                        <OrderSummaryComponentList buildItemSummary={viewModel.buildItemSummary} goToCategory={goToCategory} viewMode={0} />
                                                    }

                                                </div>
                                            </div>

                                            {
                                                !inCCLDesktopMode
                                                &&
                                                <div className="d-flex">
                                                    <div className="d-flex flex-column align-items-start w-100 justify-content-center">
                                                        <div className="mb-2">
                                                            <h2 className="main-order-summary-heading text-white d-inline-block mb-0 custom-color-light">{viewModel.isExpress ? "Your Next Day PC" : "Your Custom Build"}</h2>
                                                            <button ref={summaryExpandBtnRef} className={"toggle-summary-button d-inline-block border-0 custom-bg-dark " + summaryExpanded} type="button" data-bs-toggle="collapse" data-bs-target="#collapseOrderSummary" aria-expanded="false" aria-controls="collapseOrderSummary" aria-label="Toggle summary"
                                                                    onClick={() =>
                                                                    {
                                                                        if (summaryExpanded === "")
                                                                            setSummaryExpanded("expanded");
                                                                        else
                                                                            setSummaryExpanded("");
                                                                    }}>
                                                                <FontAwesomeIcon className="toggleSummaryButton custom-color-light" icon={icon({ name: 'chevron-up', style: 'solid' })} />
                                                            </button>
                                                        </div>

                                                        <span className="main-order-summary-price me-5 font-600 custom-text-light">&pound;{viewModel.totalCostText}
                                                            <button className="total-cost-icon position-relative bg-transparent border-0 custom-text-light" data-bs-toggle="modal" data-bs-target={"#priceChangeWarning"} aria-controls={"#priceChangeWarning"} type="button" aria-label="Open pricing infomation modal">
                                                                <FontAwesomeIcon icon={icon({ name: 'circle-info', style: 'solid' })} />
                                                            </button>
                                                        </span>

                                                        {
                                                            viewModel && viewModel.financeOptions && viewModel.monthlyPayment
                                                            &&
                                                            <span className="finance-info-container text-start">
                                                                <label className="d-inline custom-text-light">from &pound;{viewModel.monthlyPayment.toFixed(2)} per month.</label> <button type="button" className="paypal-modal-button border-0 bg-transparent text-decoration-underline px-0 custom-text-light" data-bs-toggle="modal" data-bs-target="#financeModal">More</button>
                                                            </span>
                                                        }
                                                        {
                                                            viewModel && viewModel.payPalCredit && viewModel.payPalCredit.lowestMonthlyOption && viewModel.payPalCredit.lowestMonthlyOption.monthlyPayment
                                                            &&
                                                            <span className="finance-info-container text-start">
                                                                <label className="d-inline custom-text-light">from &pound;{viewModel.payPalCredit.lowestMonthlyOption.monthlyPayment.toFixed(2)} per month.</label> <button type="button" className="paypal-modal-button border-0 bg-transparent text-decoration-underline px-0 custom-text-light" data-bs-toggle="modal" data-bs-target="#payPalCreditModal">More</button>
                                                            </span>
                                                        }
                                                        {
                                                            viewModel && viewModel.dopplePay
                                                            &&
                                                            <span className="finance-info-container text-start">
                                                                <button type="button" className="paypal-modal-button bg-transparent border-0 custom-text-light ps-0 cursor-pointer" data-bs-toggle="modal" data-bs-target="#doppleModal"><label className="custom-text-light d-inline cursor-pointer">{doppleText}</label> <span className="position-relative finance-message-icon"><FontAwesomeIcon icon={icon({ name: 'circle-info', style: 'solid' })} /></span></button>
                                                            </span>
                                                        }
                                                    </div>


                                                    <button ref={mainCTABtnOneRef} className={"position-relative text-casing primary-button custom-color-dark px-3 " + (gridListToggle === '2' ? "px-xl-4 px-xxl-5 " : "px-sm-5 ")} onClick={() => { props.handleToggleSummary(true, viewModel.options.removeFromBasket, viewModel.options.removeFromBasketBuildID); }}>
                                                        Continue
                                                        {
                                                            viewModel.missingItems && viewModel.missingItems.length > 0
                                                                ?
                                                                <span className="position-absolute primary-button-pill top-0 translate-middle badge rounded-pill bg-danger">
                                                                    {viewModel.missingItems.length} missing item{viewModel.missingItems.length > 1 && <>s</>}
                                                                    <span className="visually-hidden">missing items</span>
                                                                </span>
                                                                :
                                                                null
                                                        }
                                                    </button>
                                                </div>
                                            }

                                        </div>
                                    </div>
                                    {/* ccl image/title mini div combo */}
                                    {
                                        (inCCLDesktopMode)
                                        &&
                                        <>
                                            <div className="main-order-summary-subcontainer d-flex py-3 px-4 w-100">
                                                <img src={viewModel.settings.imageBaseUrl + viewModel.images[0] + "?width=120&height=120&scale=both"} className="pc-image d-none d-xxl-block me-3 cursor-pointer" alt="PC" onClick={handleViewImage} />

                                                <div className="d-flex flex-column w-100 me-3">

                                                    <h1 className="main-order-summary-name d-block mb-3 mt-auto"> {viewModel.isExpress ? "Your Next Day Gaming PC" : "Your Custom Build" } </h1>

                                                    <span className={"main-order-summary-price font-600 text-muted text-start" + (((viewModel?.financeOptions && viewModel?.monthlyPayment) || viewModel?.payPalCredit?.lowestMonthlyOption?.monthlyPayment) ? " mb-1" : " mb-auto")}>&pound;{viewModel.totalCostText}
                                                        <button className={"position-relative bg-transparent border-0 text-muted"} data-bs-toggle="modal" data-bs-target={"#priceChangeWarning"} aria-controls={"#priceChangeWarning"} type="button" aria-label="Open pricing infomation modal">
                                                            <FontAwesomeIcon icon={icon({ name: 'circle-info', style: 'solid' })} />
                                                        </button>
                                                    </span>

                                                    {
                                                        viewModel?.financeOptions && viewModel?.monthlyPayment
                                                        &&
                                                        <span className="finance-info-container text-start mb-auto">
                                                            <label className="text-muted d-inline">from &pound;{viewModel.monthlyPayment.toFixed(2)} per month.</label> <button type="button" className="paypal-modal-button text-muted border-0 bg-transparent text-decoration-underline px-0" data-bs-toggle="modal" data-bs-target="#financeModal">More</button>
                                                        </span>
                                                    }
                                                    {
                                                        viewModel?.payPalCredit?.lowestMonthlyOption?.monthlyPayment
                                                        &&
                                                        <span className="finance-info-container text-start mb-auto">
                                                            <label className="text-muted d-inline">from &pound;{viewModel.payPalCredit.lowestMonthlyOption.monthlyPayment.toFixed(2)} per month.</label> <button type="button" className="paypal-modal-button text-muted border-0 bg-transparent text-decoration-underline px-0" data-bs-toggle="modal" data-bs-target="#payPalCreditModal">More</button>
                                                        </span>
                                                    }

                                                </div>

                                                <button ref={mainCTABtnTwoRef} className={"position-relative primary-button custom-color-dark text-casing px-3 " + (gridListToggle === '2' ? "px-xl-4 px-xxl-5 my-auto " : "px-sm-5 ")} onClick={() => { props.handleToggleSummary(true, viewModel.options.removeFromBasket, viewModel.options.removeFromBasketBuildID); }} type="button">
                                                    CONTINUE
                                                    {
                                                        viewModel.missingItems && viewModel.missingItems.length > 0
                                                            ?
                                                            <span className="position-absolute primary-button-pill top-0 translate-middle badge rounded-pill bg-danger">
                                                                {viewModel.missingItems.length} missing item{viewModel.missingItems.length > 1 && <>s</>}
                                                                <span className="visually-hidden">missing items</span>
                                                            </span>
                                                            :
                                                            null
                                                    }
                                                </button>
                                            </div>
                                            <div className="bodge-formatting-spacer w-100 d-none d-xl-block"></div>
                                        </>
                                    }
                                </div>
                            </div>

                            {/* price change warning modal */}
                            <div className="notice-modal modal fade" id="priceChangeWarning" tabIndex="-1" aria-labelledby="priceChangeWarningModalLabel" aria-hidden="true" data-bs-backdrop="static">
                                <div className="modal-dialog modal-dialog-centered bg-transparent">
                                    <div className="modal-content">
                                        <div className="modal-body border-bottom-0 p-3 d-flex">
                                            <div className="modal-body-right-container">
                                                <div className="close-button-container mb-3">
                                                    <button type="button" className="btn-close close-button float-end" data-bs-dismiss="modal" aria-label="Close"></button>
                                                </div>
                                                <div className="px-sm-4">
                                                    <h1 className={"title mb-4" + (site === "chillblast" ? " text-light" : "")}>Live Pricing</h1>
                                                    <h5 className={"message mb-4" + (site === "chillblast" ? " text-light" : "")}>{livePricingText}</h5>
                                                    <div className="action-buttons-container">
                                                        <button type="button" className="yes-button primary-button custom-color-dark float-end px-5 py-3 mb-2 text-casing" data-bs-dismiss="modal">Okay</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>


                            { /* Welcome Modal React Style */}
                            <Modal className={site + "-modal"} show={showWelcomeModal} onHide={handleWelcomeModalClose} backdrop="static" keyboard={false}>
                                <Modal.Header className="border-bottom-0 ps-5 py-4 pe-4" style={site === "chillblast" ? {backgroundColor: "#212529"} : {}}>
                                    <Button variant="secondary" type="button" className="close-button btn-close float-end" data-bs-dismiss="modal" aria-label="Close" onClick={handleWelcomeModalClose}></Button>
                                </Modal.Header>
                                <Modal.Body className="border-bottom-0 px-5 pt-0 pb-3" style={site === "chillblast" ? {backgroundColor: "#212529"} : {}}>
                                    {
                                        expertMode
                                            ?
                                            <>
                                                <h1 className="modal-title mb-4" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>Configure your PC</h1>
                                                <p className="message mb-3" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>Welcome, the world is your oyster. Choose from thousands of options to specify your perfect PC.</p>
                                                <p className="message mb-3" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>As you choose options, the other options available to you will change, depending on what's compatible with your choices. Start wherever you like.</p>
                                            </>
                                            : viewModel.options.forceEasyMode ?
                                                <>
                                                    <h1 className="modal-title mb-4" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>Configure your PC</h1>
                                                    <p className="message mb-3" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>To start, we have provided a slimmed down list of choices to keep things simple.</p>
                                                    <p className="message mb-3" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>Start by configuring your PC from the options on the right. Options available to you will update to ensure that all the pieces of your PC work together correctly.</p>
                                                </>
                                                :
                                                <>
                                                    <h1 className="modal-title mb-4" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>Configure your PC</h1>
                                                    <p className="message mb-3" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>To start, we have provided a slimmed down list of choices to keep things simple.</p>
                                                    <p className="message mb-3" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>Start by configuring your PC from the options on the right. Options available to you will update to ensure that all the pieces of your PC work together correctly.</p>
                                                    <p className="message mb-3" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>To see all options available to you, use the toggle in the top right corner to enable our &quot;expert mode&quot;.</p>
                                                </>
                                    }
                                </Modal.Body>
                                <Modal.Footer className="modal-footer border-top-0 px-5 pb-5 pt-0" style={site === "chillblast" ? {backgroundColor: "#212529"} : {}}>

                                    <button type="button" className={"primary-button d-block w-100 text-casing" + (site === "chillblast" ? " text-dark" : "")} data-bs-dismiss="modal" onClick={() => { handleWelcomeModalClose() }}>Get Started</button>
                                    {
                                        !expertMode && !viewModel.options.forceEasyMode
                                        &&
                                        <button type="button" className="white-button d-block w-100 text-casing" style={site === "chillblast" ? {backgroundColor: "#212529", color: "rgba(222, 226, 230, 0.75)"} : {}} onClick={handleExpertToggleFromWelcome}>Enable Expert Mode</button>
                                    }
                                </Modal.Footer>
                            </Modal>

                            {/* main right panel */}
                            <div id="category-accordion" className={"main-right-panel accordion custom-bg-dark overflow-y-scroll " + optionsPanelColClass + " pt-2 pb-3 pt-lg-3 px-2 px-sm-3 px-md-4 px-lg-5 px-xl-6 overflow-auto" + panelHeightOffsetClass}>
                                {/*<div hidden={result.status === "success" ? "hidden" : ""}>{ result.status }</div>*/}

                                {/*Render top banner*/}
                                {
                                    (viewModel.bannersJSON && viewModel.bannersJSON["0"] && viewModel.bannersJSON["0"].imageUrl && viewModel.settings?.imageBaseUrl)
                                    &&
                                    <button className="border-0 p-0 mb-3"
                                            {...viewModel.bannersJSON["0"].description ? { 'data-bs-toggle': 'offcanvas', 'data-bs-target': '#offcanvasBannerInfo_0', 'aria-controls': '#offcanvasBannerInfo_0' } : {}}
                                            type="button" onClick={() => { Utils.navigateToPage(viewModel.bannersJSON["0"].navUrl ?? null) }}
                                    >
                                        <img src={viewModel.settings.imageBaseUrl + viewModel.bannersJSON["0"].imageUrl + "?scale=both"} className="img-fluid" alt="Banner Info"></img>
                                    </button>
                                }

                                {/*Render category panels*/}
                                {
                                    viewModel.buildID > 0
                                    &&
                                    viewModel.categories.map((cpmodel, index) =>
                                    {
                                        /* 718 - SSDs, 430 - Graphics, 415 - PSU, 407 - Case Cooling, 217 - Warranty, 701 - HDDs, 414 - OSs, 408 - RAM, 406 - CPU Cooling, 405 - Cases, 402 - MOBOs, 401 - CPUs, 901 - Monitors, 605 - Keyboard, 609 - Mice, 827 - Headsets, 435 - Software, 110 - Services   */
                                        //if ((viewModel.options.isExpertMode || (!viewModel.options.isExpertMode && cpmodel.showInEasyMode)) && (cpmodel.id === 401 || cpmodel.id === 405))
                                        if (viewModel.options.isExpertMode || (!viewModel.options.isExpertMode && cpmodel.showInEasyMode))
                                        {
                                            catCount++;

                                            return <CategoryPanel key={cpmodel.name}
                                                                  categoryPanelModel={cpmodel}
                                                                  updateCategory={updateCategory}
                                                                  buildID={viewModel.buildID}
                                                                  isLastItem={index + 1 === viewModel.categories.length ? true : false}
                                                                  isExpanded={categoryIdExpanded === cpmodel.id ? true : false}
                                                                  isExpandedCategoryName={viewModel.categories.find(cat => cat.id === cpmodel.id).name.replace(/\s+/g, "-") ?? null}
                                                                  isOpen={openCatId === cpmodel.id}
                                                                  handleToggleCategory={handleToggleCategory}
                                                                  isExpertMode={viewModel.options.isExpertMode}
                                                                  isViewModelUpdated={viewModel.options.isUpdated}
                                                                  isExpress={viewModel.isExpress}
                                                                  handleCategoryFetched={handleCategoryFetched}
                                                                  handleFetchIncompatibleOptions={handleFetchIncompatibleOptions}
                                                                  handleFetchCompatibleOptions={handleFetchCompatibleOptions}
                                                                  options={viewModel.options}
                                                                  settings={viewModel.settings}
                                                                  showExpertModeModal={handleDisplayExpertModeModalChange}
                                                                  setShowWelcomeModal={setShowWelcomeModal}
                                                                  showIncompatible={sessionModel.current.showIncompatibleOnCategories[cpmodel.id] ? true : false}
                                                                  categoryInfo={categoryInfo[cpmodel.id] ? categoryInfo[cpmodel.id] : null}
                                                                  scrollToItem={props.categoryItem}
                                                                  imageLoaded={props.imageLoaded}
                                                                  categoryTouched={categoryTouched}
                                                                  getSelectedOptionName={getSelectedOptionName}
                                                                  setSelectedOptionName={setSelectedOptionName}
                                                                  requiredLoadedCategories={requiredLoadedCategories}
                                                                  categoryMatchedName={categoryMatchedName}
                                                                  gridListToggle={gridListToggle}
                                                                  bannerJSON={(viewModel.bannersJSON && viewModel.bannersJSON[catCount] && viewModel.bannersJSON[catCount].imageUrl) ? viewModel.bannersJSON[catCount] : null}
                                            />
                                        }
                                        else
                                            return null;
                                    })
                                }

                                <div>
                                    {/* hidden for list view but kept styles in case added back in */}
                                    <button type="button" className={"primary-button position-relative text-casing custom-color-dark d-none mb-5" + (gridListToggle === "1" ? " d-xl-block w-100" : " px-5 ms-auto")} onClick={() => { props.handleToggleSummary(true); }}>
                                        Continue
                                        {
                                            viewModel.missingItems && viewModel.missingItems.length > 0
                                                ?
                                                <span className="position-absolute primary-button-pill large-button-pill top-0 translate-middle badge rounded-pill bg-danger">
                                                    {viewModel.missingItems.length} missing item{viewModel.missingItems.length > 1 && <>s</>}
                                                    <span className="visually-hidden">missing items</span>
                                                </span>
                                                :
                                                null
                                        }
                                    </button>
                                </div>
                                {
                                    viewModel.chillblastXHTML && parse(viewModel.chillblastXHTML)

                                }

                                {
                                    viewModel.getInTouchHTML && parse(viewModel.getInTouchHTML)
                                }
                            </div>
                        </div>

                        { /* Incompatibility Modal React Style */}
                        <Modal className={site + "-modal"} show={showIncompatibilityModal} onHide={() => { handleCloseIncompatibilityModal(false) }} backdrop="static" keyboard={false}>
                            <Modal.Header className="border-bottom-0 ps-5 py-4 pe-4">
                                <Button variant="secondary" type="button" className="close-button btn-close float-end" data-bs-dismiss="modal" aria-label="Close" onClick={() => { handleCloseIncompatibilityModal(false) }}></Button>
                            </Modal.Header>
                            <Modal.Body className="border-bottom-0 px-5 pt-0 pb-3">
                                <h1 className="modal-title mb-4" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>The chosen "{incompatibilities ? incompatibilities.selectedProductName : "item"}" is not compatible with one or more components.</h1>
                                <label className="modal-top-label text-muted mb-4 fw-bold text-casing">Incompatible Components</label>
                                <div className="modal-items-container row">

                                    {/* Render incompatible categories headings and option images */}
                                    {
                                        incompatibilities && incompatibilities.incompatibleItems && incompatibilities.incompatibleItems.length > 0
                                            ?
                                            incompatibilities.incompatibleItems.map((item, index) =>
                                            {
                                                return (
                                                    <div key={"item_" + index} className="incompatible-item-container col-4 d-flex flex-column align-items-center text-center position-relative mb-4">
                                                        <FontAwesomeIcon className="incompatible-item-icon position-absolute text-danger" icon={icon({ name: 'circle-exclamation', style: 'solid' })} />
                                                        <img src={viewModel.settings.imageBaseUrl + item.imageUrl + "?width=300&height=300&scale=both"} className="d-block w-100 my-auto" alt="PC"></img>
                                                        <h2 className="incompatible-item-category-name mt-3 mb-0" style={site === "chillblast" ? {color: "#f8f9fa"} : {}}>{item.categoryName}</h2>
                                                    </div>
                                                );
                                            })
                                            :
                                            null
                                    }

                                </div>

                                <div className="incompatible-details-container">

                                    <button className="toggle-incompatibility-details-button d-flex border-0 fw-semibold px-0 mb-2" style={site === "chillblast" ? {backgroundColor: "#212529", color: "rgba(222, 226, 230, 0.75)"} : {}}
                                            onClick={() => { incompatibilityDetailsExpanded === "" ? setIncompatibilityDetailsExpanded("expanded") : setIncompatibilityDetailsExpanded("") }}
                                            type="button" data-bs-toggle="collapse" data-bs-target="#collapseIncompatibilityDetails" aria-expanded="false" aria-controls="collapseIncompatibilityDetails"
                                    >More details <FontAwesomeIcon className={"incompatibility-details-button-icon ms-1 align-self-center " + incompatibilityDetailsExpanded} icon={icon({ name: 'chevron-down', style: 'solid' })} style={site === "chillblast" ? {color: "#f8f9fa"} : {}} /></button>

                                    <div id="collapseIncompatibilityDetails" className="collapse mb-3">
                                        {/* Render reason text */}
                                        {
                                            incompatibilities && incompatibilities.incompatibleItems && incompatibilities.incompatibleItems.length > 0
                                                ?
                                                incompatibilities.incompatibleItems.map((item, index) =>
                                                {

                                                    if (item.reason != null)
                                                    {
                                                        item.reason = item.reason.replace("{", " <span class='text-danger'>");
                                                        item.reason = item.reason.replace("}", " </span>");
                                                    }

                                                    if (item.rightReason != null)
                                                    {
                                                        item.rightReason = item.rightReason.replace("{", " <span class='text-danger'>");
                                                        item.rightReason = item.rightReason.replace("}", " </span>");
                                                    }

                                                    return (
                                                        <div key={"itemReason_" + index} className="mb-2">
                                                            <h5 className="incompatible-product-name mb-0 fw-semibold" style={site === "chillblast" ? {color: "#fff"} : {}} >{item.productName}</h5>
                                                            <p className="incompatible-reason-text mb-0" dangerouslySetInnerHTML={{ __html: `${item.reason} ${item.rightReason}` }}></p>
                                                        </div>
                                                    );
                                                })
                                                :
                                                null
                                        }
                                    </div>
                                </div>
                                <p className="modal-warning-message text-muted mb-0">{displayAddComponentBtn() ? "Making this selection will remove your current selection(s) that are incompatible, please confirm this or cancel." : "Your current selection(s) are incompatible."}</p>
                            </Modal.Body>
                            <Modal.Footer className="modal-footer border-top-0 px-5 pb-5 pt-0">
                                <button type="button" className="d-block primary-button w-100 py-3 m-0 mb-2 text-casing" style={site === "chillblast" ? {color: "#212529"} : {}} data-bs-dismiss="modal" onClick={() => { handleCloseIncompatibilityModal(false) }}>{displayAddComponentBtn() ? "No, cancel my selection" : "Okay"}</button>
                                {displayAddComponentBtn() && <button type="button" className="d-block white-button w-100 py-3 mx-0 mt-0 text-casing" style={site === "chillblast" ? {backgroundColor: "#212529", color: "rgba(222, 226, 230, 0.75)"} : {}}  onClick={() => { handleCloseIncompatibilityModal(true) }}>Yes, select this component</button> }
                            </Modal.Footer>
                        </Modal>

                        <PaymentInfoModals novunaOptions={viewModel?.financeOptions} novunaTerms={viewModel?.financeModalTerms} paypalOptions={viewModel?.payPalCredit?.creditOptions} dopplePay={viewModel?.dopplePay} doppleZeroInterest={viewModel?.doppleZeroInterest} />

                        {
                            (viewModel.bannersJSON && viewModel.bannersJSON["0"] && viewModel.bannersJSON["0"].description)
                            &&
                            <div className="offcanvas offcanvas-end" tabIndex="-1" id="offcanvasBannerInfo_0" aria-labelledby="offcanvasBannerInfo_0">
                                <div className="offcanvas-header p-3 pb-2 p-md-5 pb-md-3">
                                    <div className={!viewModel.bannersJSON["0"].title ? "d-none" : ""}>
                                        <h1 className="offcanvas-title mb-2 me-2 me-sm-3" dangerouslySetInnerHTML={{ __html: viewModel.bannersJSON["0"].title }} />
                                    </div>
                                    <button type="button" className={"offcanvas-close-button btn-close me-1" + (!viewModel.bannersJSON["0"].title ? " ms-auto" : "")} data-bs-dismiss="offcanvas" aria-label="Close"></button>
                                </div>
                                <div className="offcanvas-body d-flex flex-column pt-2 px-3 px-md-5 pb-5">
                                    <span dangerouslySetInnerHTML={{ __html: viewModel.bannersJSON["0"].description }} ></span>
                                </div>
                            </div>
                        }
                    </main>

                    {
                        (viewModel.images?.length > 0 && viewModel.settings?.imageBaseUrl)
                        &&
                        <ViewingGallery images={viewModel.images} imageBaseUrl={viewModel.settings.imageBaseUrl} isVisible={viewImageIsActive} handleClose={handleViewingGalleryClose} />
                    }

                </>

            }
        </>
    );

}